import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { DashboardWrapper } from '@/components/DashboardWrapper';
import PageContainer from '@/components/PageContainer';
import { IUserContext, useUserContext } from '@/context/UserContext';
import { get } from '@/utils/queries';
import {
  Client,
  MarketplaceVendorsCategory,
  MarketplaceVendorsDTO,
} from '@types';
import { Button, Input } from 'react-daisyui';
import { useMemo, useState } from 'react';
import { DashboardCard } from '@/components/DashboardCard';
import { EmptyState } from '@/components/EmptyState';
import ErrorAlert from '@/components/ErrorAlert/ErrorAlert';
import { Spinner } from '@/components/Spinner';
import { MarketplaceVendorItem } from '@/pages/Marketplace/MarketplaceVendorItem';
import { vendorCategoryList } from '@/pages/Marketplace/MarketPlaceData';

export default function MarketPlace() {
  const [activeTab, setActiveTab] = useState(vendorCategoryList[0]);
  const [searchText, setSearchText] = useState('');
  const { companyName }: IUserContext = useUserContext(); // Use the context

  const { data: clients } = get<Client[]>('account/list');
  const {
    data: vendorItemsData,
    isLoading: isLoadingVendors,
    error,
  } = get<MarketplaceVendorsDTO[]>('marketplacevendors/');

  const itemsData = useMemo(() => {
    if (!vendorItemsData) return [];
    return vendorItemsData.filter(
      (item) =>
        item.name.toLowerCase().includes(searchText.toLowerCase()) &&
        (item.category.includes(
          activeTab.tag as unknown as MarketplaceVendorsCategory
        ) ||
          activeTab.tag.toString() === 'All Tools')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorItemsData, activeTab, searchText]);

  if (error) {
    return (
      <PageContainer name='marketplace'>
        <DashboardWrapper
          companyName={companyName as string}
          companyData={clients}
        >
          <ErrorAlert />
        </DashboardWrapper>
      </PageContainer>
    );
  }

  if (!vendorItemsData?.length) {
    return (
      <PageContainer name='marketplace'>
        <DashboardWrapper
          companyName={companyName as string}
          companyData={clients}
        >
          <EmptyState name='marketplace' />
        </DashboardWrapper>
      </PageContainer>
    );
  }

  if (isLoadingVendors) {
    return <Spinner />;
  }

  return (
    <PageContainer name='marketplace'>
      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
      <DashboardWrapper
        companyName={companyName as string}
        companyData={clients}
      >
        <div className='p-4'>
          <div className='flex w-full justify-between items-center mb-2.5'>
            <h1 className='text-lg font-bold uppercase'>
              <FontAwesomeIcon
                icon={faStore}
                size='lg'
                className='mr-4 text-white'
              />
              MarketPlace
            </h1>
          </div>
          <DashboardCard className='p-4' cardBodyclassName='p-0'>
            <div className='mt-3 mb-1'>
              <div className='mb-3'>
                <h2 className='font-bold text-left mb-2 text-xl'>
                  OpStart&apos;s Vendor Marketplace
                </h2>
                <h3 className='text-left mb-4'>
                  OpStart curates the best tools for startups and negotiates
                  exclusive discounts for clients
                </h3>
              </div>

              <div className='relative flex text-gray-600 focus-within:text-gray-400'>
                <div className='flex justify-between w-full'>
                  <div className='relative flex mb-4'>
                    <span className='absolute inset-y-0 left-[260px] flex items-center pl-2'>
                      <button
                        type='submit'
                        className='p-1 focus:outline-none focus:shadow-outline'
                      >
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                      </button>
                    </span>
                    <Input
                      data-testid='client-search-input'
                      className='border-accent w-[300px]'
                      placeholder='Search Tools'
                      size='sm'
                      defaultValue={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                    />
                  </div>
                </div>
              </div>

              {vendorCategoryList.map((tabName, index) => {
                return (
                  <span
                    key={`${index + 1}_category_span`}
                    className='mr-2 mb-2'
                  >
                    <Button
                      size='sm'
                      onClick={() => setActiveTab(tabName)}
                      className={`${
                        tabName.tag === activeTab.tag ? 'btn-active' : ''
                      }`}
                      key={`${index + 1}_category_button`}
                    >
                      {tabName.tag}
                    </Button>
                  </span>
                );
              })}

              <div className='grid grid-cols-3'>
                {itemsData.map((item) => (
                  <MarketplaceVendorItem
                    vendorItemData={item}
                    key={item.name}
                  />
                ))}
              </div>

              {activeTab.tag !== 'All Tools' ? (
                <>
                  <hr className='my-6 mx-2' />
                  <div className='w-full mt-6 mx-2'>
                    <span className='text-xl'>
                      <strong>Our {activeTab.tag} Recommendations:</strong>
                    </span>
                    <br />
                    {activeTab.description ? (
                      <p className='mt-2'>{activeTab.description}</p>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          </DashboardCard>
        </div>
      </DashboardWrapper>
    </PageContainer>
  );
}
